import { api, apiCall, apiPrefix } from "@services/API/Requests";
import { MapContact } from "@services/API/MapData";
import { handleResponse } from "@services/API/StatusCodes";
import { store } from "@services/StateManagement/Store";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { USER_TYPE } from "@constants/Enums";
import { TENANTDEPARTMENT } from "@types/tenantDepartment";

export const getContactsData = async (
  setContactData: (data: any) => void,
  setError: (error: any) => void,
  setLoading: (loading: boolean) => void,
) => {
  return apiCall({
    method: "get",
    url: `${apiPrefix}/contacts/users?user_type=2`,
    setLoading,
    onSuccess: (response) => {
      const contact = response.data.contacts.map(MapContact);
      setContactData(contact);
      return handleResponse(response, "電話帳");
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const getContactData = async (
  setContactData: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  userId: string,
  setLocalContactData?: (data: any) => void,
) => {
  if (!userId) return;

  return apiCall({
    method: "get",
    url: `${apiPrefix}/users/show_contact/${userId}`,
    setLoading,
    onSuccess: (response) => {
      const contact = response.data;

      let position_name =
        Array.isArray(response.data.external_departments) &&
        response.data.external_departments[0]?.external_name
          ? response.data.external_departments[0].external_name
          : Array.isArray(response.data.internal_departments) &&
            response.data.internal_departments[0]?.external_name
          ? response.data.internal_departments[0].external_name
          : "";

      const user = MapContact(contact, position_name);

      setContactData(user);

      if (setLocalContactData) {
        setLocalContactData(user);
      }
      return handleResponse(response, "電話帳");
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const getFilteredContactsData = async (
  setContactsData: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  query: string,
) => {
  const params = new URLSearchParams();
  const uri = `${apiPrefix}/users/?${query}&${params.toString()}`;

  return apiCall({
    method: "get",
    url: uri,
    setLoading,
    onSuccess: (response) => {
      const contacts = response.data.contacts.map(MapContact);
      setContactsData(contacts);
      return handleResponse(response, "電話帳");
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const postContact = async (
  contactData: any,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  forceCreate: boolean = false,
  userType: USER_TYPE = USER_TYPE.EXTERNAL,
  roleId?: number,
  tenantDepartmentData?: TENANTDEPARTMENT,
) => {
  if (!contactData) return;

  const { id, uid, name, furigana_name, ...filteredData } = contactData;

  filteredData.user_type = userType;

  const dataToSend = {
    contact: {
      ...filteredData,
      ...(roleId && { role_id: roleId }),
      ...(tenantDepartmentData && {
        user_tenant_id: tenantDepartmentData.tenant_department_id,
        position_name: tenantDepartmentData.position_name,
      }),
      force_create: forceCreate,
    },
  };

  return apiCall({
    method: "post",
    url: `${apiPrefix}/users/create_contact`,
    data: dataToSend,
    setLoading,
    idReturn: true,
    onSuccess: (response) => {
      const responseMessage = handleResponse(response, "電話帳");
      return {
        ...responseMessage,
        user_id: response.data?.created_users?.user?.id,
      };
    },
    onError: (errorResponse) => {
      const error =
        errorResponse?.failed_users?.[0]?.error ||
        errorResponse.errors ||
        errorResponse.error ||
        errorResponse;

      setError(TranslateErrorMessage(error));
    },
  });
};

export const postContactArray = async (
  contactData: any[],
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (contactData) {
    let response;
    let responseMessage;
    let failedUsers;
    let createdUsers;
    let request_id;

    const userDataArray = contactData.map((user) => {
      const { uid, name, furigana_name, ...filteredUserData } = user.userInfo;

      return {
        ...filteredUserData,
      };
    });

    try {
      const header = store.getState().api.headers;
      api.defaults.headers = header;

      setLoading(true);
      response = await api.post(`${apiPrefix}/users/create_with_csv`, {
        user: userDataArray,
      });

      responseMessage = handleResponse(response, "電話帳");
      createdUsers = response?.data?.created_users;
      failedUsers = response?.data?.failed_users;
      request_id = response?.data?.request_id;
    } catch (err) {
      console.error(err.message);
      failedUsers = err.response.data.failed_users;
      const errorResponse =
        err.response.data.errors || err.response.data.error || err.message;
      responseMessage = { status: err.status, message: errorResponse };
      setError(errorResponse);
    } finally {
      setLoading(false);

      if (failedUsers != null) {
        if (failedUsers.length > 1) {
          failedUsers.forEach((user) => {
            const translatedMessage = TranslateErrorMessage(user.error[0]);
            user.error = translatedMessage;
          });
        } else {
          const translatedMessage = TranslateErrorMessage(failedUsers.error[0]);
          failedUsers.error = translatedMessage;
        }
      }
      return {
        responseMessage,
        failed_users: failedUsers,
        created_users: createdUsers,
        request_id: request_id,
      };
    }
  }
};

export const postContactArrayCSV = async (
  contactData: any[],
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (contactData) {
    let response;
    let responseMessage;
    let failedUsers;
    let createdUsers;
    let request_id;

    const userDataArray = contactData.map((user) => {
      const { uid, name, furigana_name, ...filteredUserData } = user.userInfo;

      return {
        ...filteredUserData,
      };
    });

    try {
      const header = store.getState().api.headers;
      api.defaults.headers = header;

      setLoading(true);
      response = await api.post(`${apiPrefix}/users/create_with_csv`, {
        user: userDataArray,
      });
      responseMessage = handleResponse(response, "電話帳");
      createdUsers = response?.data?.created_users;
      failedUsers = response?.data?.failed_users;
      request_id = response?.data?.request_id;
    } catch (err) {
      console.error(err.message);
      failedUsers = err.response.data.failed_users;
      const errorResponse =
        err.response.data.errors || err.response.data.error || err.message;
      responseMessage = { status: err.status, message: errorResponse };
      setError(errorResponse);
    } finally {
      setLoading(false);

      if (failedUsers != null) {
        if (failedUsers.length > 1) {
          failedUsers.forEach((user) => {
            const translatedMessage = TranslateErrorMessage(user.error[0]);
            user.error = translatedMessage;
          });
        } else {
          const translatedMessage = TranslateErrorMessage(failedUsers.error[0]);
          failedUsers.error = translatedMessage;
        }
      }
      return {
        responseMessage,
        failed_users: failedUsers,
        created_users: createdUsers,
        request_id: request_id,
      };
    }
  }
};

export const putContact = async (
  contactData: any,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  contact_id: string,
  forceCreate: boolean = false,
  userType: USER_TYPE = USER_TYPE.EXTERNAL,
) => {
  if (!contactData) return;

  const {
    id,
    uid,
    name,
    furigana_name,
    login_id,
    login_password,
    ...filteredData
  } = contactData;

  filteredData.user_type = userType;

  const dataToSend = {
    user: {
      ...filteredData,
      force_create: forceCreate,
    },
  };

  return apiCall({
    method: "put",
    url: `${apiPrefix}/users/${contact_id}`,
    data: dataToSend,
    setLoading,
    onSuccess: (response) => {
      const responseMessage = handleResponse(response, "電話帳");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const deleteContactData = async (
  selectedUsers: number[],
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
) => {
  let response;
  let responseMessage;
  setLoading(true);
  try {
    const header = store.getState().api.headers;
    api.defaults.headers = header;

    response = await Promise.all(
      selectedUsers.map((contact) =>
        api.delete(`/v1/contacts/${contact}`).catch((err) => err.response),
      ),
    );

    const errorResponse = response.find((res) => res && res.status >= 400);

    if (errorResponse) {
      setError(errorResponse.data?.message);
      return errorResponse;
    } else {
      setError(null);
    }
  } catch (err) {
    console.error(err.message);
    const errorResponse =
      err.response.data.errors || err.response.data.error || err.message;
    responseMessage = { status: err.status, message: errorResponse };
    setError(errorResponse);
  } finally {
    response = {
      status: 200,
    };
    responseMessage = handleResponse(response, "電話帳");
    setLoading(false);
    return responseMessage;
  }
};
